import { useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Music, Disc, Headphones, Check, ArrowLeft, Bot, Camera, Upload, User, Radio, Megaphone, BarChart3, Gift } from 'lucide-react';
import axios from 'axios';
import AuthFooter from '../components/layout/AuthFooter';

const MAX_ACCOUNT_TYPES = 5;

const accountTypes = [
  {
    id: 'artist',
    title: 'Artist',
    description: 'Upload & sell your music',
    icon: Music,
    color: '#00CCFF',
    features: ['Upload unlimited tracks & videos', 'Keep ~90% of sales', 'Build your fanbase']
  },
  {
    id: 'producer',
    title: 'Producer',
    description: 'Sell beats & instrumentals',
    icon: Disc,
    color: '#ff6b6b',
    features: ['Sell beats to artists', 'License management', 'Producer profile page']
  },
  {
    id: 'dj',
    title: 'DJ',
    description: 'Get booked for gigs',
    icon: Radio,
    color: '#00CCFF',
    features: ['DJ profile & showcase', 'Get booked for events', 'Upload mixes & sets']
  },
  {
    id: 'promoter',
    title: 'Promoter',
    description: 'Promote events & book talent',
    icon: Megaphone,
    color: '#ff6b6b',
    features: ['Event management', 'Book DJs & artists', 'Sell tickets & manage venues']
  },
  {
    id: 'ai_artist',
    title: 'AI Artist',
    description: 'Share AI-generated music',
    icon: Bot,
    color: '#9b59b6',
    features: ['Upload AI-generated tracks', 'Featured on AI Radio', 'Sell with Suno verification']
  },
  {
    id: 'advertiser',
    title: 'Advertiser',
    description: 'Run ad campaigns',
    icon: BarChart3,
    color: '#22c55e',
    features: ['Create video & audio ads', 'Target radio listeners', 'Analytics dashboard']
  },
  {
    id: 'fan',
    title: 'Fan',
    description: 'Discover & support artists',
    icon: Headphones,
    color: '#ffd93d',
    features: ['Stream free previews', 'Purchase to own', 'Create playlists']
  }
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('type') || null;
  const referralCode = searchParams.get('ref') || null;
  // Where to send the user after a successful signup. Defaults to /home.
  // Only same-origin relative paths are accepted (open-redirect guard).
  const nextParam = searchParams.get('next') || '';
  const safeNext = nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/home';
  
  const [step, setStep] = useState(defaultType ? 2 : 1);
  const [selectedTypes, setSelectedTypes] = useState(defaultType ? [defaultType] : []);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one account type');
      return;
    }
    
    if (!profilePhoto) {
      toast.error('Please upload a profile photo');
      return;
    }
    
    setLoading(true);
    
    try {
      // Register with profile photo in a single request
      const formData = new FormData();
      formData.append('username', username);
      formData.append('email', email);
      formData.append('password', password);
      formData.append('account_type', selectedTypes.join(','));
      if (gender) formData.append('gender', gender);
      formData.append('profile_photo', profilePhoto);
      
      const response = await axios.post(`${API}/auth/register-with-photo`, formData);
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      
      // Auto-redeem referral code if present in URL
      if (referralCode && token) {
        try {
          await axios.post(`${API}/auth/redeem-referral-on-signup`, 
            { referral_code: referralCode },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Referral code applied! You earned a promotion boost.');
        } catch (err) {
          // Silently fail - user still registered successfully
        }
      }
      
      toast.success('Welcome to One Voice Distribution!');
      navigate(safeNext);
      window.location.reload(); // Refresh to update auth context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(t => t !== typeId);
      }
      if (prev.length >= MAX_ACCOUNT_TYPES) {
        toast.error(`Maximum ${MAX_ACCOUNT_TYPES} account types allowed`);
        return prev;
      }
      return [...prev, typeId];
    });
  };

  const selectedTypeObjects = accountTypes.filter(t => selectedTypes.includes(t.id));

  return (
    <div className="dark-surface min-h-screen bg-black flex flex-col lg:flex-row">
      {/* Left side - Info */}
      <div className="lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-zinc-900 to-black p-6 lg:p-12 flex flex-col justify-center">
        <div className="max-w-md mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-white mb-8 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} />
            <span className="text-sm">Back to home</span>
          </Link>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Join the Movement
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Independent music distribution. No gatekeepers. ~90% to artists.
          </p>
          
          {referralCode && (
            <div className="mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2" data-testid="referral-banner">
              <Gift size={16} className="text-green-400 flex-shrink-0" />
              <p className="text-green-400 text-sm font-mono">
                Referral code <span className="font-bold">{referralCode}</span> detected! You'll both get a promotion boost.
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Check size={16} className="text-cyan-500" />
              </div>
              <div>
                <h3 className="text-white font-medium">Upload Your Sound</h3>
                <p className="text-gray-500 text-sm">Share your music with the world instantly</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Check size={16} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-white font-medium">Keep Your Earnings</h3>
                <p className="text-gray-500 text-sm">~90% of every content sale goes directly to you</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Check size={16} className="text-purple-500" />
              </div>
              <div>
                <h3 className="text-white font-medium">Build Your Fanbase</h3>
                <p className="text-gray-500 text-sm">Connect directly with fans who support you</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 lg:px-16 py-8 lg:py-0 pb-20 lg:pb-0">
        <div className="max-w-md mx-auto w-full">
          <img 
            src="/onevoice-logo-circular.png" 
            alt="One Voice Distribution"
            className="h-16 w-16 mb-6 object-contain"
            data-testid="register-logo"
          />
          
          {step === 1 ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-1">
                Choose Your Path
              </h2>
              <p className="text-gray-500 text-sm mb-1">
                Select your account types (up to {MAX_ACCOUNT_TYPES})
              </p>
              <p className="text-gray-600 text-xs mb-6">
                {selectedTypes.length}/{MAX_ACCOUNT_TYPES} selected
                {selectedTypes.length > 0 && (
                  <span className="text-cyan-500 ml-2">
                    {selectedTypes.map(t => accountTypes.find(a => a.id === t)?.title).join(', ')}
                  </span>
                )}
              </p>
              
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {accountTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedTypes.includes(type.id);
                  const isDisabled = !isSelected && selectedTypes.length >= MAX_ACCOUNT_TYPES;
                  return (
                    <button
                      key={type.id}
                      onClick={() => toggleType(type.id)}
                      disabled={isDisabled}
                      className={`w-full p-3 rounded-lg border transition-all text-left ${
                        isSelected 
                          ? 'border-2 bg-zinc-900' 
                          : isDisabled
                            ? 'border-zinc-800 bg-zinc-900/30 opacity-50 cursor-not-allowed'
                            : 'border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900'
                      }`}
                      style={{ borderColor: isSelected ? type.color : undefined }}
                      data-testid={`account-type-${type.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${type.color}20` }}
                        >
                          <Icon size={18} style={{ color: type.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium text-sm">{type.title}</h3>
                          <p className="text-gray-500 text-xs truncate">{type.description}</p>
                        </div>
                        <div 
                          className={`w-5 h-5 rounded flex items-center justify-center transition-all border ${
                            isSelected ? 'border-transparent' : 'border-zinc-600'
                          }`}
                          style={{ backgroundColor: isSelected ? type.color : 'transparent' }}
                        >
                          {isSelected && <Check size={12} className="text-black" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <Button
                onClick={() => selectedTypes.length > 0 && setStep(2)}
                disabled={selectedTypes.length === 0}
                className="w-full h-12 mt-4 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold disabled:opacity-50"
                data-testid="continue-btn"
              >
                Continue ({selectedTypes.length} selected)
              </Button>
            </>
          ) : (
            <>
              <button 
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-gray-500 hover:text-white mb-4 transition-colors text-sm"
              >
                <ArrowLeft size={14} />
                Change account type
              </button>
              
              {selectedTypeObjects.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedTypeObjects.map(st => (
                    <div 
                      key={st.id}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${st.color}20`, border: `1px solid ${st.color}`, color: st.color }}
                    >
                      <st.icon size={12} />
                      {st.title}
                    </div>
                  ))}
                </div>
              )}
              
              <h2 className="text-2xl font-bold text-white mb-1">
                Create Account
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Enter your details to get started
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Profile Photo Upload */}
                <div className="flex flex-col items-center mb-4">
                  <p className="text-gray-400 text-sm mb-3">Upload your profile photo *</p>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="relative cursor-pointer group"
                  >
                    <div className={`w-24 h-24 rounded-full overflow-hidden border-2 ${profilePhotoPreview ? 'border-cyan-500' : 'border-zinc-700 border-dashed'} flex items-center justify-center bg-zinc-900 transition-colors group-hover:border-cyan-400`}>
                      {profilePhotoPreview ? (
                        <img src={profilePhotoPreview} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User size={32} className="text-zinc-600" />
                      )}
                    </div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center border-2 border-black">
                      <Camera size={14} className="text-black" />
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="profile-photo-input"
                  />
                  <p className="text-zinc-600 text-xs mt-2">Click to upload (max 5MB)</p>
                </div>
                
                <Input
                  data-testid="username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12"
                  placeholder="Username"
                />
                <Input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12"
                  placeholder="Email address"
                />
                <Input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-12"
                  placeholder="Password (min 6 characters)"
                />

                {/* Gender — optional. Used by the fallback-avatar engine so
                    if you never upload a picture, the auto-assigned
                    portrait matches you. Stored on the user; can be changed
                    later in Settings. */}
                <div>
                  <label className="block text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-1.5">
                    Gender <span className="text-zinc-700 normal-case tracking-normal">(optional · used for fallback avatar)</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="register-gender-group">
                    {[
                      { v: 'male', label: 'Male' },
                      { v: 'female', label: 'Female' },
                      { v: 'non_binary', label: 'Non-binary' },
                      { v: 'prefer_not_to_say', label: 'Skip' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setGender(opt.v)}
                        className={`px-3 py-2 border font-mono text-xs uppercase tracking-wider transition-colors ${
                          gender === opt.v
                            ? 'border-[#00CCFF] bg-[#00CCFF]/10 text-[#00CCFF]'
                            : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                        }`}
                        data-testid={`register-gender-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading || !profilePhoto}
                  className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold disabled:opacity-50"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
                
                {!profilePhoto && (
                  <p className="text-yellow-500 text-xs text-center">Profile photo is required</p>
                )}
              </form>
              
              {selectedTypeObjects.length > 0 && (
                <div className="mt-6 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <p className="text-gray-400 text-xs mb-2">
                    Your account includes:
                  </p>
                  <ul className="space-y-1">
                    {selectedTypeObjects.flatMap(st => 
                      st.features.map((feature, idx) => (
                        <li key={`${st.id}-${idx}`} className="flex items-center gap-2 text-gray-300 text-sm">
                          <Check size={12} style={{ color: st.color }} />
                          {feature}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="mt-6 pt-6 border-t border-zinc-800 text-center pb-16">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <Link 
                to={nextParam ? `/login?next=${encodeURIComponent(nextParam)}` : '/login'}
                className="text-cyan-500 hover:text-cyan-400 font-medium"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  );
}
